import { useMemo } from "react";

import { Info24px } from "@similarweb/icons";
import { SecureShield } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { TextLink } from "@similarweb/ui-components";
import { Flex, Text, Stack } from "@similarweb/ui-components/foundations";

import { getPackageHref } from "app/common/hrefs";
import { swSettings } from "app/common/services/swSettings";
import { PackageFeatureInfo } from "app/components/PackageSwitcher/components/PackageFeatureInfo/PackageFeatureInfo";
import { SummarySwitchCard } from "app/components/PackageSwitcher/components/SummarySwitchCard/SummarySwitchCard";
import type { FeatureArea } from "app/components/PackageSwitcher/consts/packageFeatureInfo";
import { PaymentPeriod } from "app/components/PackageSwitcher/consts/paymentPeriod";
import type { IAddOn } from "app/components/PackageSwitcher/types/addonsTypes";
import { getCurrencySign } from "app/components/PackageSwitcher/utils/getCurrencySign";
import type {
    IGetCurrentPackageSwitchInformationResponse,
    ISwitchPackageOptions,
} from "app/custom-hooks/switchPackage/useSwitchPackagePopup.types";
import { useSwitchPackagePriceCalculation } from "app/custom-hooks/switchPackage/useSwitchPackagePriceCalculation";
import { allTrackers } from "app/services/track/track";
import { isPendingTrialStatus } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { getSwitchInfoText } from "../helpers";

import {
    InfoHolder,
    PackageSwitcherContent,
    SecurePaymentWrapper,
    TextLinkWithoutIcon,
} from "./PackageSwitcher.styled";

const TERMS_OF_USE_URL = "https://www.similarweb.com/corp/legal/terms/";

interface IPackageSwitcherProps {
    switchInformationData: IGetCurrentPackageSwitchInformationResponse;
    packageToChange: ISwitchPackageOptions;
    availableAddOns: IAddOn[];
    featureArea: FeatureArea;
}

export const PackageSwitcher = ({
    switchInformationData,
    packageToChange,
    availableAddOns,
    featureArea,
}: IPackageSwitcherProps) => {
    const translate = useTranslation();
    const isUpgradeToAnnual = packageToChange.period === PaymentPeriod.ANNUAL;

    const currencySign = getCurrencySign(switchInformationData.currentPackage.currency);

    const {
        initialPackagePriceFormatted,
        targetPackageName,
        totalPrice,
        annualDiscountFormatted,
        addonsInfo,
        addonsPriceFormatted,
        taxFormatted,
        relativePriceWithTax,
    } = useSwitchPackagePriceCalculation({
        isUpgradeToAnnual,
        currencySign,
        packageToChange,
        availableAddOns,
    });

    const infoText = useMemo(
        () =>
            getSwitchInfoText({
                targetPackage: packageToChange,
                totalPrice,
                relativePriceWithTax,
                isAddonsIncluded: availableAddOns.length > 0,
                isCCWallUser: isPendingTrialStatus(swSettings),
                currencySign,
            }),
        [packageToChange, totalPrice, relativePriceWithTax, availableAddOns.length, currencySign],
    );
    const handleTermsOfUseClick = () => {
        allTrackers.dispatchTrackEvent("popup", "click", "terms of service/one click package modal");
    };
    const handleViewAllPackagesClick = () => {
        allTrackers.dispatchTrackEvent("popup", "click", "view all packages/one click package modal");
    };

    return (
        <PackageSwitcherContent>
            <PackageFeatureInfo featureArea={featureArea} />
            <SummarySwitchCard
                targetPackageName={targetPackageName}
                initialPackagePriceFormatted={initialPackagePriceFormatted}
                hasAddons={availableAddOns.length > 0}
                addonsInfo={addonsInfo}
                period={packageToChange.period}
                addonsPriceFormatted={addonsPriceFormatted}
                isUpgradeToAnnual={isUpgradeToAnnual}
                annualDiscountFormatted={annualDiscountFormatted}
                totalPrice={totalPrice}
                taxFormatted={taxFormatted}
                taxPercentage={packageToChange.taxPercentage}
            />
            <Stack>
                <InfoHolder>
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Info24px
                            size="sm"
                            color={colorsPalettes.carbon[300]}
                        />
                    </Flex>
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[400]}
                        dangerouslySetInnerHTML={{ __html: infoText }}
                    />
                </InfoHolder>
                <SecurePaymentWrapper>
                    <Flex gap="4px">
                        <SecureShield
                            size="xs"
                            color={colorsPalettes.mint[400]}
                        />
                        <Text fontVariant="body2">{translate("package.switcher.secure.payment")}</Text>
                    </Flex>
                    <Flex
                        gap="2px"
                        alignItems="center"
                    >
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("no_touch_purchase_modal.summary.you_accept")}
                        </Text>
                        <TextLinkWithoutIcon
                            href={TERMS_OF_USE_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-automation="package-switcher-tos-link"
                            translations={{ opensInNewTab: "" }}
                            onClick={handleTermsOfUseClick}
                        >
                            {translate("package.switcher.terms.of.service")}
                        </TextLinkWithoutIcon>
                    </Flex>
                </SecurePaymentWrapper>
                <Stack
                    alignItems="center"
                    padding={"16px 0 8px"}
                >
                    <Flex gap="2px">
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("package.switcher.want.to.explore")}
                        </Text>
                        <TextLink
                            href={getPackageHref()}
                            target="_blank"
                            rel="noreferrer"
                            data-automation="package-switcher-view-all-packages-link"
                            translations={{ opensInNewTab: "" }}
                            onClick={handleViewAllPackagesClick}
                        >
                            {translate("package.switcher.view.all.packages")}
                        </TextLink>
                    </Flex>
                </Stack>
            </Stack>
        </PackageSwitcherContent>
    );
};
